import { BrowserRouter, HashRouter, Navigate, Outlet, Route, Routes } from 'react-router-dom';
import { FC, lazy, Suspense } from 'react';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { DndProvider } from 'react-dnd';
import { CircularProgress } from '@mui/material';
import { ChunkErrorBoundary } from './ChunkErrorBoundary';

import {
  CalendlySchedulePage,
  PaywallPage,
  SettingsPage,
  StripeCheckoutSuccessPage,
} from '../pages';
import App from '../App';
import { RequireAuth, LoginContainer } from '.';
import {
  CreateEventMenuProvider,
  EventDetailsMenuProvider,
  RecurringEventScopeModalProvider,
  TeamProvider,
} from '../data-access';
import { useBreakpoints } from '../utils';
import { LoadingOverlay, NotSupported, SideNavigation } from '../components';
import SideNavigationAddFab from '../components/SideNavigation/SideNavigationAddFab';
import SideNavigationQuickActionModals from '../components/SideNavigation/SideNavigationQuickActionModals';
import { CallbackRoutes } from './callback.routings';
import { BasisProvider } from './basisProvider';
import { OnboardingRoutes } from './onboarding.routings';
import RequireSubscription from './RequireSubscription';
import { useFeatureFlag } from '../utils';
import PendingTeamInviteRedirectConsumer from './PendingTeamInviteRedirectConsumer';
import TeamInviteEntryRoute from './TeamInviteEntryRoute';
import './routing.scss';

const LazyDashboard = lazy(() => import('../pages/DashboardPage/DashboardPage'));
const LazyTeamPage = lazy(() => import('../pages/TeamPage/TeamPage'));
const LazyDataPage = lazy(() => import('../pages/DataView/DataView'));
const LazyTaskPage = lazy(() => import('../pages/TaskPage/TaskPage'));
const LazyRoutinePage = lazy(() => import('../pages/RoutinePage/RoutinePage'));

const NewTabsProviderWrapper: FC = () => {
  return (
    <CreateEventMenuProvider>
      <EventDetailsMenuProvider>
        <RecurringEventScopeModalProvider>
          <DndProvider backend={HTML5Backend}>
            <div className="new-tabs-provider-wrapper">
              <PendingTeamInviteRedirectConsumer />
              <SideNavigation />
              <SideNavigationAddFab />
              <SideNavigationQuickActionModals />
              <Outlet />
            </div>
          </DndProvider>
        </RecurringEventScopeModalProvider>
      </EventDetailsMenuProvider>
    </CreateEventMenuProvider>
  );
};

export const AppRouter: FC = () => {
  const { isDesktop } = useBreakpoints();
  const { isFeatureFlagsLoading, isTeamPlanUser } = useFeatureFlag();
  const Router = window.location.protocol === 'file:' ? HashRouter : BrowserRouter;

  const renderTeamApp = () => (
    <TeamProvider>
      <LazyTeamPage />
    </TeamProvider>
  );

  const renderTeamPage = () => {
    if (isTeamPlanUser) {
      return renderTeamApp();
    }

    if (isFeatureFlagsLoading) {
      return <LoadingOverlay />;
    }

    return <Navigate to="../calendar" replace />;
  };

  const renderNewOnboardingRouters = () => {
    return (
      <Route path="/*">
        <Route element={<RequireAuth />}>
          <Route path={'subscribe'} element={<PaywallPage />} />
          <Route path={'stripe-checkout/success'} element={<StripeCheckoutSuccessPage />} />
          <Route element={<RequireSubscription />}>
            <Route element={isDesktop ? <Outlet /> : <NotSupported />}>
              <Route element={<BasisProvider />}>
                <Route path="home" element={<NewTabsProviderWrapper />}>
                  <Route index element={<Navigate to="calendar" replace />} />
                  <Route
                    path="calendar"
                    element={
                      <ChunkErrorBoundary>
                        <Suspense fallback={<div className="routing-loader"><CircularProgress /></div>}>
                          <LazyDashboard />
                        </Suspense>
                      </ChunkErrorBoundary>
                    }
                  />
                  <Route path="teams" element={renderTeamPage()} />
                  <Route path="teams/:teamId" element={renderTeamPage()} />
                  <Route path="teams/:teamId/:teamView" element={renderTeamPage()} />
                  <Route
                    path="data"
                    element={
                      <ChunkErrorBoundary>
                        <Suspense fallback={<div className="routing-loader"><CircularProgress /></div>}>
                          <LazyDataPage />
                        </Suspense>
                      </ChunkErrorBoundary>
                    }
                  />
                  <Route
                    path="tasks"
                    element={
                      <ChunkErrorBoundary>
                        <Suspense fallback={<div className="routing-loader"><CircularProgress /></div>}>
                          <LazyTaskPage />
                        </Suspense>
                      </ChunkErrorBoundary>
                    }
                  />
                  <Route
                    path="routine"
                    element={
                      <ChunkErrorBoundary>
                        <Suspense fallback={<div className="routing-loader"><CircularProgress /></div>}>
                          <LazyRoutinePage />
                        </Suspense>
                      </ChunkErrorBoundary>
                    }
                  />
                  <Route path="settings/*" element={<SettingsPage />} />
                </Route>
              </Route>
            </Route>
          </Route>
        </Route>
        <Route path="onboarding/*" element={<OnboardingRoutes />} />
      </Route>
    );
  };

  return (
    <Router>
      <Routes>
        <Route path="/" element={<App />}>
          <Route index element={<LoginContainer />}></Route>
          <Route path="/home/teams/invite/*" element={<TeamInviteEntryRoute />}>
            <Route element={isDesktop ? <Outlet /> : <NotSupported />}>
              <Route element={<BasisProvider />}>
                <Route element={<NewTabsProviderWrapper />}>
                  <Route index element={renderTeamApp()} />
                  <Route path="*" element={renderTeamApp()} />
                </Route>
              </Route>
            </Route>
          </Route>
          {renderNewOnboardingRouters()}
          <Route path="/callback/*" element={<CallbackRoutes />} />
          <Route path={'/calendly/:calendlyIntegId'} element={<CalendlySchedulePage />} />
        </Route>
      </Routes>
    </Router>
  );
};
